// Validates the `kin_tool_event` payload kin-chat's create_todo tool (issue
// #691) sends on its own dedicated SSE field — see kin-chat/index.ts's STEP 10
// dispatch loop and executeCreateTodoTool. Deliberately NOT delivered inside
// the assistant's text content: a Copilot review on this PR caught an earlier
// version that embedded a [TAG]{json}[/TAG] string in the streamed text, which
// shares a channel with model-authored text — a forged tag inside quoted/
// echoed content (a web search result, pasted email) could have triggered a
// fake "Added ✓" toast, or an Undo button deleting an arbitrary row id. This
// module just validates shape; the channel separation is what actually closes
// that off (see streamChat's onToolEvent in Index.tsx).

// #692 adds task_confirm/task_receipt for create_scheduled_task — same
// channel, same validation posture, two task kinds (reminder_nudge is a
// single row; appointment_tracker is one item that may join an existing
// shared row, so its receipt carries wasNewRow for Undo to know whether to
// delete the row or just remove this item from it).

export interface ReminderNudgeTaskRow {
  task_type: "reminder_nudge";
  display_name: string;
  config: { task_description: string; help_types: string[] };
  next_run_at: string;
  is_active: boolean;
}

/**
 * ⚠️ A `type`, not an `interface`, and `completion_history` is `number[]`.
 * Both are load-bearing (#1360).
 *
 * `Index.tsx` writes `config: { items: [item] }` into `scheduled_tasks`, whose
 * `config` column is typed `Json`. An `interface` has no implicit index
 * signature, so it can never satisfy `Json` however JSON-shaped its members
 * are; a `type` alias can. And `unknown[]` is not `Json[]` either, so the field
 * had to be honest before the whole thing could be.
 *
 * `number[]` is not a loosening — it is what the column actually holds.
 * `src/types/scheduledTaskConfig.ts` (#892) types the same array as `number[]`
 * on all four sibling item types, having verified it against the five append
 * sites in TasksTab.tsx: `[...(item.completion_history || []), actualInterval]`
 * where `actualInterval` is a `Math.round(...)`, and every initializer is `[]`.
 */
export type AppointmentTrackerItem = {
  id: string;
  name: string;
  category: "dentist" | "doctor";
  interval_days: number;
  last_done: string;
  remind_days_before: number;
  who_for: string;
  provider_name: string;
  provider_phone: string | null;
  completion_history: number[];
  learned_interval: number | null;
};

// #694 adds complete_confirm/reschedule_confirm for complete_item/
// reschedule_item. Neither ever has a *_receipt counterpart — these two
// tools ALWAYS confirm, at every autonomy level, no auto-execute path exists
// server-side at all (the Aug 7 "complete/reschedule always confirm"
// decision). Scoped to the three tables that already have a clean, single
// reschedulable timestamp column.
export type CompletableTable = "notes_todos" | "todo_list_items" | "task_queue_items";

export type KinToolEvent =
  | { type: "todo_confirm"; item: string; listId: string | null; listName: string | null }
  | {
      type: "todo_receipt";
      item: string;
      id: string;
      table: "notes_todos" | "todo_list_items";
      listId: string | null;
      listName: string | null;
    }
  | { type: "task_confirm"; taskKind: "reminder_nudge"; row: ReminderNudgeTaskRow }
  | { type: "task_confirm"; taskKind: "appointment_tracker"; item: AppointmentTrackerItem }
  | { type: "task_receipt"; taskKind: "reminder_nudge"; id: string; taskDescription: string; nextRunAt: string }
  | {
      type: "task_receipt";
      taskKind: "appointment_tracker";
      id: string;
      itemId: string;
      label: string;
      providerName: string;
      wasNewRow: boolean;
    }
  // #890 STEP 2 — save_family_fact's receipt. No *_confirm counterpart: this
  // tool only ever runs because she explicitly asked, so there is nothing to
  // confirm. Carries the row id so Undo archives THAT row rather than matching
  // on category+key the way the [LEARN] chip does — that matcher takes out
  // every sibling of a multi-valued key (#747), so undoing a second allergy
  // would remove the first.
  | {
      type: "fact_receipt";
      /**
       * Which write produced it. #915.
       *   "tool"  — save_family_fact, i.e. she asked. Renders a toast+Undo.
       *   "learn" — passive [LEARN] capture. Renders the inline chip instead,
       *             because a toast for something she never asked for is noise.
       * Both carry the row id; only the presentation differs.
       */
      source: "tool" | "learn";
      id: string;
      category: string;
      subject: string | null;
      key: string;
      value: string;
      updated: boolean;
    }
  // #1451/#1452 — kin-chat wrote one night of her pending meal plan, or
  // committed it. The chat's Save & commit bar listens for both.
  | { type: "meal_plan_night_updated"; runId: string; day: string; mealType: string; mealName: string | null; removed: boolean }
  | { type: "meal_plan_committed"; runId: string }
  // #1586 — add_grocery_items wrote her default grocery list. No confirm
  // counterpart: like to-dos since #1137, it only ever arrives pre-written.
  // `restored` rows were un-checked rather than inserted, so Undo re-checks
  // them instead of deleting a line she already had.
  | {
      type: "grocery_receipt";
      added: Array<{ id: string; item: string; restored: boolean }>;
      alreadyOn: string[];
    }
  | {
      type: "complete_confirm";
      table: CompletableTable;
      id: string;
      label: string;
      /** #1654 — the durable `pending_tool_confirms` row this chip answers.
       *  OPTIONAL on purpose: a stream produced before #1654 deployed carries no
       *  id, and must still render a working chip rather than being dropped. */
      pendingConfirmId?: string;
    }
  | {
      type: "reschedule_confirm";
      /** #1654 — see complete_confirm. Optional for the same reason. */
      pendingConfirmId?: string;
      table: CompletableTable;
      id: string;
      label: string;
      newTimestamp: string;
      newTimeDescription: string;
      priorTimestamp: string | null;
    };

function parseReminderNudgeRow(raw: unknown): ReminderNudgeTaskRow | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (obj.task_type !== "reminder_nudge") return null;
  if (typeof obj.display_name !== "string") return null;
  if (typeof obj.next_run_at !== "string" || !obj.next_run_at) return null;
  if (typeof obj.is_active !== "boolean") return null;
  const cfg = obj.config as Record<string, unknown> | undefined;
  if (!cfg || typeof cfg.task_description !== "string" || !cfg.task_description.trim()) return null;
  if (!Array.isArray(cfg.help_types)) return null;
  return {
    task_type: "reminder_nudge",
    display_name: obj.display_name,
    config: { task_description: cfg.task_description, help_types: cfg.help_types as string[] },
    next_run_at: obj.next_run_at,
    is_active: obj.is_active,
  };
}

function parseAppointmentItem(raw: unknown): AppointmentTrackerItem | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;
  if (typeof obj.id !== "string" || !obj.id) return null;
  if (typeof obj.name !== "string" || !obj.name.trim()) return null;
  if (obj.category !== "dentist" && obj.category !== "doctor") return null;
  if (typeof obj.interval_days !== "number") return null;
  if (typeof obj.last_done !== "string") return null;
  if (typeof obj.remind_days_before !== "number") return null;
  if (typeof obj.who_for !== "string") return null;
  if (typeof obj.provider_name !== "string" || !obj.provider_name.trim()) return null;
  return {
    id: obj.id,
    name: obj.name,
    category: obj.category,
    interval_days: obj.interval_days,
    last_done: obj.last_done,
    remind_days_before: obj.remind_days_before,
    who_for: obj.who_for,
    provider_name: obj.provider_name,
    provider_phone: typeof obj.provider_phone === "string" ? obj.provider_phone : null,
    completion_history: Array.isArray(obj.completion_history) ? obj.completion_history : [],
    learned_interval: typeof obj.learned_interval === "number" ? obj.learned_interval : null,
  };
}

/** Validates an unknown value into a KinToolEvent, or null if it doesn't
 *  match any known shape — defensive parsing against a malformed/partial
 *  JSON chunk, not a security boundary (this is server-authored data on a
 *  channel the model can't write to). */
export function parseKinToolEvent(raw: unknown): KinToolEvent | null {
  if (!raw || typeof raw !== "object") return null;
  const obj = raw as Record<string, unknown>;

  if (obj.type === "todo_confirm" || obj.type === "todo_receipt") {
    if (typeof obj.item !== "string" || !obj.item.trim()) return null;
    const listId = typeof obj.listId === "string" ? obj.listId : null;
    const listName = typeof obj.listName === "string" ? obj.listName : null;

    if (obj.type === "todo_confirm") {
      return { type: "todo_confirm", item: obj.item, listId, listName };
    }
    if (typeof obj.id !== "string" || !obj.id) return null;
    if (obj.table !== "notes_todos" && obj.table !== "todo_list_items") return null;
    return { type: "todo_receipt", item: obj.item, id: obj.id, table: obj.table, listId, listName };
  }

  if (obj.type === "fact_receipt") {
    if (typeof obj.id !== "string" || !obj.id) return null;
    if (typeof obj.key !== "string" || !obj.key.trim()) return null;
    if (typeof obj.value !== "string" || !obj.value.trim()) return null;
    if (typeof obj.category !== "string" || !obj.category.trim()) return null;
    if (typeof obj.updated !== "boolean") return null;
    // Unknown/absent source defaults to "tool" — the pre-#915 shape, so an
    // older server mid-deploy still produces a working toast rather than being
    // dropped on the floor.
    const source = obj.source === "learn" ? "learn" : "tool";
    return {
      type: "fact_receipt",
      source,
      id: obj.id,
      category: obj.category,
      // A blank subject is a household-wide fact, not a malformed event —
      // normalized to null rather than rejected.
      subject:
        typeof obj.subject === "string" && obj.subject.trim() ? obj.subject.trim() : null,
      key: obj.key,
      value: obj.value,
      updated: obj.updated,
    };
  }

  if (obj.type === "task_confirm") {
    if (obj.taskKind === "reminder_nudge") {
      const row = parseReminderNudgeRow(obj.row);
      return row ? { type: "task_confirm", taskKind: "reminder_nudge", row } : null;
    }
    if (obj.taskKind === "appointment_tracker") {
      const item = parseAppointmentItem(obj.item);
      return item ? { type: "task_confirm", taskKind: "appointment_tracker", item } : null;
    }
    return null;
  }

  if (obj.type === "task_receipt") {
    if (typeof obj.id !== "string" || !obj.id) return null;
    if (obj.taskKind === "reminder_nudge") {
      if (typeof obj.taskDescription !== "string" || !obj.taskDescription.trim()) return null;
      if (typeof obj.nextRunAt !== "string" || !obj.nextRunAt) return null;
      return { type: "task_receipt", taskKind: "reminder_nudge", id: obj.id, taskDescription: obj.taskDescription, nextRunAt: obj.nextRunAt };
    }
    if (obj.taskKind === "appointment_tracker") {
      if (typeof obj.itemId !== "string" || !obj.itemId) return null;
      if (typeof obj.label !== "string" || !obj.label.trim()) return null;
      if (typeof obj.providerName !== "string" || !obj.providerName.trim()) return null;
      if (typeof obj.wasNewRow !== "boolean") return null;
      return {
        type: "task_receipt",
        taskKind: "appointment_tracker",
        id: obj.id,
        itemId: obj.itemId,
        label: obj.label,
        providerName: obj.providerName,
        wasNewRow: obj.wasNewRow,
      };
    }
    return null;
  }

  const isCompletableTable = (t: unknown): t is CompletableTable =>
    t === "notes_todos" || t === "todo_list_items" || t === "task_queue_items";

  if (obj.type === "meal_plan_night_updated") {
    if (typeof obj.run_id !== "string" || !obj.run_id) return null;
    if (typeof obj.day !== "string" || !obj.day.trim()) return null;
    const meal = (obj.meal && typeof obj.meal === "object" ? obj.meal : {}) as Record<string, unknown>;
    return {
      type: "meal_plan_night_updated",
      runId: obj.run_id,
      day: obj.day,
      mealType: typeof obj.meal_type === "string" ? obj.meal_type : "dinner",
      mealName: typeof meal.meal_name === "string" && meal.meal_name.trim() ? meal.meal_name : null,
      removed: meal.removed === true,
    };
  }

  if (obj.type === "meal_plan_committed") {
    if (typeof obj.run_id !== "string" || !obj.run_id) return null;
    return { type: "meal_plan_committed", runId: obj.run_id };
  }

  if (obj.type === "grocery_receipt") {
    if (!Array.isArray(obj.added) || !Array.isArray(obj.alreadyOn)) return null;
    const added: Array<{ id: string; item: string; restored: boolean }> = [];
    for (const a of obj.added) {
      const r = (a ?? {}) as Record<string, unknown>;
      // One malformed row drops the whole event rather than rendering a
      // receipt whose Undo would skip a row that was really written.
      if (typeof r.id !== "string" || !r.id) return null;
      if (typeof r.item !== "string" || !r.item.trim()) return null;
      added.push({ id: r.id, item: r.item, restored: r.restored === true });
    }
    const alreadyOn = obj.alreadyOn.filter((x): x is string => typeof x === "string" && x.trim() !== "");
    if (added.length === 0 && alreadyOn.length === 0) return null;
    return { type: "grocery_receipt", added, alreadyOn };
  }

  if (obj.type === "complete_confirm") {
    if (typeof obj.id !== "string" || !obj.id) return null;
    if (typeof obj.label !== "string" || !obj.label.trim()) return null;
    if (!isCompletableTable(obj.table)) return null;
    return {
      type: "complete_confirm",
      table: obj.table,
      id: obj.id,
      label: obj.label,
      // #1654 — carried through when present, ABSENT rather than null when not.
      // A pre-#1654 stream has no id and must still render a working chip, so
      // this is deliberately not a validity requirement.
      ...(typeof obj.pendingConfirmId === "string" && obj.pendingConfirmId
        ? { pendingConfirmId: obj.pendingConfirmId }
        : {}),
    };
  }

  if (obj.type === "reschedule_confirm") {
    if (typeof obj.id !== "string" || !obj.id) return null;
    if (typeof obj.label !== "string" || !obj.label.trim()) return null;
    if (!isCompletableTable(obj.table)) return null;
    if (typeof obj.newTimestamp !== "string" || !obj.newTimestamp) return null;
    if (typeof obj.newTimeDescription !== "string" || !obj.newTimeDescription.trim()) return null;
    const priorTimestamp = typeof obj.priorTimestamp === "string" ? obj.priorTimestamp : null;
    return {
      type: "reschedule_confirm",
      table: obj.table,
      id: obj.id,
      label: obj.label,
      newTimestamp: obj.newTimestamp,
      newTimeDescription: obj.newTimeDescription,
      priorTimestamp,
      // #1654 — see complete_confirm above.
      ...(typeof obj.pendingConfirmId === "string" && obj.pendingConfirmId
        ? { pendingConfirmId: obj.pendingConfirmId }
        : {}),
    };
  }

  return null;
}
